import { CleaningZoneApi } from '@common/interfaces/robot/rooms/cleaning-zone';
import { currentVersionManagement, managementApiInstance } from './axios-base';
import FloorData, { FloorApiData } from '@common/interfaces/robot/rooms/floor-data';
import { RoomApiData } from '@common/interfaces/robot/rooms/room-data';
import { RedZoneApi } from '@common/interfaces/robot/rooms/red-zone';
import { RuleZoneApi } from '@common/interfaces/robot/rooms/rule-zone';
import { LandmarkApi } from '@common/interfaces/robot/rooms/landmark';
import { WaypointEdge, WaypointNodeApi } from '@common/interfaces/robot/rooms/waypoints';
import { MissionApiData } from '@common/interfaces/robot/missions/mission';
import { ScheduleEntryApi } from '@common/interfaces/robot/scheduling/schedule-entry';
import { ParamListApi } from '@common/interfaces/robot/configurations/param-list';
import { RoomLimitZoneApi } from '@common/interfaces/robot/rooms/room-limit-zone';
import { EnvironmentPollution } from '@common/interfaces/robot/cleaning-configurations/environment-pollution';
import NavLocalPlanner from '@common/interfaces/configuration/nav/nav-local-planner';
import NavTasks from '@common/interfaces/configuration/nav/nav-tasks';
import DeepPartial from '@common/utils/deep-partial';
import ExcludedDate from '@common/interfaces/robot/excluded-dates/excluded-date';
import { ButtonApiConfiguration } from '@common/interfaces/robot/buttons/command-buttons';
import { TeachAndRepeatZonePathApi } from '@common/interfaces/robot/rooms/teach-and-repeat-zone-path';
import { GateSetApi } from '@common/interfaces/robot/rooms/gate';
import PaginationWrapper from 'models/misc/api/pagination-wrapper';
import Error from '@common/interfaces/misc/errors';

class LocalRobotDataService {
  async getFloorsByRobotId(id: string): Promise<FloorApiData[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/floors`,
    );
    return response.data;
  }

  async patchFloorByRobotId(
    id: string,
    floorId: string,
    data: Partial<FloorApiData>,
  ): Promise<Partial<FloorData>> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${id}/floors/${floorId}/`,
      data,
    );
    return response.data;
  }

  async patchRoomById(
    id: string,
    roomId: string,
    data: DeepPartial<RoomApiData>,
  ): Promise<Partial<RoomApiData>> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${id}/rooms/${roomId}/`,
      data,
    );
    return response.data;
  }

  async deleteRobotMissionById(robotId: string, missionId: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/robots/${robotId}/missions/${missionId}/`,
    );
    return response.data;
  }

  async postNewRobotMission(robotId: string, mission: MissionApiData): Promise<MissionApiData> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/robots/${robotId}/missions/`,
      mission,
    );
    return response.data;
  }

  async patchRobotMission(
    robotId: string,
    missionId: string,
    mission: Partial<MissionApiData>,
  ): Promise<MissionApiData> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${robotId}/missions/${missionId}/`,
      mission,
    );
    return response.data;
  }

  async getGlobalMissions(id: string): Promise<MissionApiData[]> {
    // fetches the global missions
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/missions/`,
    );
    return response.data;
  }

  async deleteRobotScheduleById(robotId: string, scheduleId: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/robots/${robotId}/schedules/${scheduleId}/`,
    );
    return response.data;
  }

  async postNewRobotSchedule(
    robotId: string,
    scheduleEntry: ScheduleEntryApi,
  ): Promise<ScheduleEntryApi> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/robots/${robotId}/schedules/`,
      scheduleEntry,
    );
    return response.data;
  }

  async patchRobotSchedule(
    robotId: string,
    scheduleId: string,
    schedule: Partial<ScheduleEntryApi>,
  ): Promise<ScheduleEntryApi> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${robotId}/schedules/${scheduleId}/`,
      schedule,
    );
    return response.data;
  }

  async enableAllSchedules(
    robotId: string,
    floorId: string,
  ): Promise<{ enabled: boolean; floor_id: string }> {
    const response = await managementApiInstance.patch<{ enabled: boolean; floor_id: string }>(
      `/${currentVersionManagement}/robots/${robotId}/schedules/enable-all/?floor_id=${floorId}`,
      {},
    );
    return response.data;
  }

  async disableAllSchedules(
    robotId: string,
    floorId: string,
  ): Promise<{ enabled: boolean; floor_id: string }> {
    const response = await managementApiInstance.patch<{ enabled: boolean; floor_id: string }>(
      `/${currentVersionManagement}/robots/${robotId}/schedules/disable-all/?floor_id=${floorId}`,
      {},
    );
    return response.data;
  }

  async postNewCleaningZone(
    robotId: string,
    repeatZonePath: CleaningZoneApi & {
      teachAndRepeatPath?: TeachAndRepeatZonePathApi;
    },
  ): Promise<CleaningZoneApi & { teachAndRepeatPath?: TeachAndRepeatZonePathApi }> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/robots/${robotId}/cleaning-zones/`,
      repeatZonePath,
    );
    return response.data;
  }

  async patchCleaningZone(
    robotId: string,
    zoneId: string,
    repeatZonePath: Partial<CleaningZoneApi> & {
      teachAndRepeatPath?: Partial<TeachAndRepeatZonePathApi>;
    },
  ): Promise<CleaningZoneApi & { teachAndRepeatPath?: TeachAndRepeatZonePathApi }> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${robotId}/cleaning-zones/${zoneId}/`,
      repeatZonePath,
    );
    return response.data;
  }

  async deleteRobotCleaningZoneById(robotId: string, zoneId: string): Promise<RoomApiData> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/robots/${robotId}/cleaning-zones/${zoneId}/`,
    );
    return response.data;
  }

  async postNewRedZone(robotId: string, redZone: RedZoneApi): Promise<RedZoneApi> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/robots/${robotId}/restricted-zones/`,
      redZone,
    );
    return response.data;
  }

  async patchRedZone(
    robotId: string,
    zoneId: string,
    redZone: Partial<RedZoneApi>,
  ): Promise<RedZoneApi> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${robotId}/restricted-zones/${zoneId}/`,
      redZone,
    );
    return response.data;
  }

  async deleteRobotRedZoneById(robotId: string, zoneId: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/robots/${robotId}/restricted-zones/${zoneId}/`,
    );
    return response.data;
  }

  async patchLimitZone(
    robotId: string,
    zoneId: string,
    limitZone: Partial<RoomLimitZoneApi>,
  ): Promise<RoomLimitZoneApi> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${robotId}/limit-zones/${zoneId}/`,
      limitZone,
    );
    return response.data;
  }

  async postNewRuleZone(robotId: string, ruleZone: RuleZoneApi): Promise<RuleZoneApi> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/robots/${robotId}/rule-zones/`,
      ruleZone,
    );
    return response.data;
  }

  async patchRuleZone(
    robotId: string,
    zoneId: string,
    ruleZone: Partial<RuleZoneApi>,
  ): Promise<RuleZoneApi> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${robotId}/rule-zones/${zoneId}/`,
      ruleZone,
    );
    return response.data;
  }

  async deleteRobotRuleZoneById(robotId: string, zoneId: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/robots/${robotId}/rule-zones/${zoneId}/`,
    );
    return response.data;
  }

  async postNewLandmark(robotId: string, landmark: LandmarkApi): Promise<LandmarkApi> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/robots/${robotId}/landmarks/`,
      landmark,
    );
    return response.data;
  }

  async patchLandmark(
    robotId: string,
    landmarkId: string,
    landmark: Partial<LandmarkApi>,
  ): Promise<LandmarkApi> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${robotId}/landmarks/${landmarkId}/`,
      landmark,
    );
    return response.data;
  }

  async deleteRobotLandmarkById(robotId: string, landmarkId: string): Promise<RoomApiData> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/robots/${robotId}/landmarks/${landmarkId}/`,
    );
    return response.data;
  }

  async upsertWaypointSet(
    robotId: string,
    waypointSet: { room: string; waypointNodes: WaypointNodeApi[]; waypointEdges: WaypointEdge[] },
  ): Promise<{ waypointNodes: WaypointNodeApi[]; waypointEdges: WaypointEdge[] }> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/robots/${robotId}/waypoints/`,
      waypointSet,
    );
    return response.data;
  }

  async deleteRobotGateById(robotId: string, gateId: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/robots/${robotId}/gates/${gateId}/`,
    );
    return response.data;
  }

  async saveRobotGateById(robotId: string, gateSet: GateSetApi): Promise<GateSetApi> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/robots/${robotId}/gates/`,
      gateSet,
    );
    return response.data;
  }

  async patchRobotGateById(robotId: string, gateSet: GateSetApi): Promise<GateSetApi> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${robotId}/gates/${gateSet.gate.id}/`,
      gateSet,
    );
    return response.data;
  }

  async getErrors({
    robotId,
    page,
  }: {
    robotId: string;
    page: number;
  }): Promise<PaginationWrapper<Error>> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${robotId}/errors`,
      {
        params: { page },
      },
    );
    return response.data;
  }

  async getParamList(id: string): Promise<ParamListApi> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/param-list`,
    );
    return response.data;
  }

  async patchParamList(id: string, data: Partial<ParamListApi>): Promise<ParamListApi> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${id}/param-list/update/`,
      data,
    );
    return response.data;
  }

  async getEnvironmentPollution(id: string): Promise<EnvironmentPollution[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/cleaning-configuration/environment-pollution`,
    );
    return response.data;
  }

  async patchCustomEnvironmentPollution(
    id: string,
    data: Partial<EnvironmentPollution>,
  ): Promise<EnvironmentPollution> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${id}/cleaning-configuration/environment-pollution/update/`,
      data,
    );
    return response.data;
  }

  async getNavLocalPlanner(id: string): Promise<NavLocalPlanner> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/nav/local-planner`,
    );
    return response.data;
  }

  async patchNavLocalPlanner(
    id: string,
    data: DeepPartial<NavLocalPlanner>,
  ): Promise<NavLocalPlanner> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${id}/nav/local-planner/update/`,
      data,
    );
    return response.data;
  }

  async getNavTasks(id: string): Promise<NavTasks> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/nav/tasks`,
    );
    return response.data;
  }

  async patchNavTasks(id: string, data: DeepPartial<NavTasks>): Promise<NavTasks> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${id}/nav/tasks/update/`,
      data,
    );
    return response.data;
  }

  async getExcludedDates(id: string): Promise<ExcludedDate[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/excluded-dates`,
    );
    return response.data;
  }

  async postExcludedDate(id: string, data: ExcludedDate): Promise<ExcludedDate> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/robots/${id}/excluded-dates/`,
      data,
    );
    return response.data;
  }

  async patchExcludedDateById(
    id: string,
    dateId: string,
    data: Partial<ExcludedDate>,
  ): Promise<ExcludedDate> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${id}/excluded-dates/${dateId}/`,
      data,
    );
    return response.data;
  }

  async deleteExcludedDateById(id: string, dateId: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/robots/${id}/excluded-dates/${dateId}/`,
    );
    return response.data;
  }

  async getButtonConfiguration(id: string): Promise<ButtonApiConfiguration> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/button-configuration/`,
    );
    return response.data;
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new LocalRobotDataService();
